import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useFormik } from 'formik';

import Spinner from '../../shared/spinner.component';

import { lastClientIdSelector } from '../../userData/store/selectors';
import DetailPopup from '../../shared/detailPopup.component';
import FormikProvider from '../../shared/forms/formikProvider.component';
import Form from '../../shared/forms/form.component';
import { YesNoFlag } from '../../shared/types';
import { ClientId } from '../../administration/clientPage/types';
import { FormSchema } from '../../shared/forms/types';
import { useUserHelpClients } from '../../shared/queries/helpPage';

import { createHelpContentSettingsSchema } from './helpContentSettings.schema';
import { ALL_CLIENTS } from './constants';

export type HelpContentSettings = {
  clients: (ClientId|typeof ALL_CLIENTS)[],
}

type SettingsFormValue = {
  restrictClients: YesNoFlag,
  clients: number[],
}

type HelpContentSettingsPopupProps = {
  settings: HelpContentSettings,
  onChange: (settings: HelpContentSettings) => void,
  popupId: string,
  Footer?: React.FC
}

type HelpContentSettingsPopupContentProps = {
  formik: ReturnType<typeof useFormik>,
  schema: FormSchema,
  editedSettings: HelpContentSettings,
  popupId: string,
  Footer?: React.FC
}

const HelpContentSettingsPopupContent = (
  { formik, schema, editedSettings, popupId, Footer }: HelpContentSettingsPopupContentProps
) => {
  const { setFieldValue } = formik;

  useEffect(() => {
    Object.entries(editedSettings).forEach(([field, value]) => {
      setFieldValue(field, value);
    });
  }, [editedSettings, setFieldValue]);

  return (
    <DetailPopup
      className="help-content-settings-popup"
      id={popupId}
      title="Settings"
      size="sm"
      place="bottom"
      offset={{ left: 40 }}
      onHidePopup={formik.handleSubmit}
    >
      <div>
        <Form
          // @ts-ignore Fix when Form proptypes will be rewritten to typescript
          formik={formik}
          schema={schema}
          openForEdit
        />
        <div className="footer-container">
          {Footer}
        </div>
      </div>
    </DetailPopup>

  );
};

const isClientId = (arg: ClientId | typeof ALL_CLIENTS): arg is ClientId => typeof arg === 'number';

const toFormikValues = ({ clients }: HelpContentSettings) => ({
  restrictClients: clients.includes(ALL_CLIENTS) ? YesNoFlag.N : YesNoFlag.Y,
  clients: clients.includes(ALL_CLIENTS)
    ? []
    : clients.filter(isClientId),
});

const fromFormikValues = ({ restrictClients, clients }: SettingsFormValue) => ({
  clients: restrictClients === YesNoFlag.Y ? clients : [ALL_CLIENTS],
});

export const HelpContentSettingsPopup = ({
  settings,
  onChange,
  popupId,
  Footer,
}: HelpContentSettingsPopupProps) => {
  const { data: clients, isLoading } = useUserHelpClients();
  const currentClientId = useSelector(lastClientIdSelector);
  const settingsFormValue: SettingsFormValue = useMemo(() => toFormikValues(settings), [settings]);

  const onSubmit = useCallback((formValue: SettingsFormValue) => {
    onChange(fromFormikValues(formValue));
  }, [onChange]);

  const schema = useMemo(
    () => createHelpContentSettingsSchema(clients, currentClientId),
    [clients, currentClientId]
  );

  // replace any with proper formik types after rewrite formik utils to typescript
  const renderForm = useCallback(({ formik, schema }) => (
    <HelpContentSettingsPopupContent
      formik={formik}
      schema={schema}
      editedSettings={settingsFormValue}
      popupId={popupId}
      Footer={Footer}
    />
  ), [settingsFormValue, popupId, Footer]);

  return isLoading
    ? (<Spinner/>)
    : (
      <FormikProvider
        schema={schema}
        onSubmit={onSubmit}
        render={renderForm}
      />
    );
};
